import { ApiMeEmployee } from "@/types/employees/employee.types.ts";
import { ApiNotificationChannelType, NotificationSettings } from "@/types/notification.types.ts";
import { ApiPermission, PermissionType } from "@/types/role.types.ts";
import { ApiFeatureType, ApiPlanFeatures } from "@/types/subscription.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import i18n from "i18next";
import { useTranslation } from "react-i18next";

export interface NotificationSettingItem {
  key: keyof NotificationSettings;
  title: string;
  description: string;
  availableOptions: ApiNotificationChannelType[];
  isInApp?: boolean;
  anyOfViewPermissions?: ApiPermission[];
  anyPlanFeatures?: (keyof ApiPlanFeatures)[];
}

export interface NotificationSettingSection {
  title: string;
  items: NotificationSettingItem[];
}

export type NotificationChannelItem = SelectItem;
export const NotificationChannelItems = (): NotificationChannelItem[] => {
  return [
    {
      id: ApiNotificationChannelType.slack,
      title: i18n.t("integrations:slack_title"),
    },
    {
      id: ApiNotificationChannelType.email,
      title: i18n.t("core:email_short"),
    },
    {
      id: ApiNotificationChannelType.messenger,
      title: i18n.t("core:messenger"),
    },
  ];
};

export default function useNotificationsSettingService() {
  const { t } = useTranslation();
  const getNotificationsSettingSections = (): NotificationSettingSection[] => {
    return [
      {
        title: t("common:general"),
        items: [
          {
            key: "event_all",
            title: t("calendar:calendar_events"),
            description: t("calendar:notification_settings_calendar_events_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            anyPlanFeatures: [ApiFeatureType.ats, ApiFeatureType.core_company_calendar],
          },
        ],
      },
      {
        title: t("core:team"),
        items: [
          {
            key: "time_off_all",
            title: t("time_off:time_off"),
            description: t("time_off:notification_settings_time_off_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            isInApp: true,
            anyPlanFeatures: [ApiFeatureType.time_off],
          },
          {
            key: "blog_post_all",
            title: t("blog:news_and_updates"),
            description: t("blog:notification_settings_blog_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            isInApp: true,
            anyPlanFeatures: [ApiFeatureType.blog],
          },
          {
            key: "task_all",
            title: t("tasks:tasks"),
            description: t("tasks:notification_settings_tasks_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            anyPlanFeatures: [ApiFeatureType.tasks],
          },
          {
            key: "task_overdue",
            title: t("tasks:tasks_overdue"),
            description: t("tasks:notification_settings_tasks_overdue_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            anyPlanFeatures: [ApiFeatureType.tasks],
          },
        ],
      },
      {
        title: t("goals:goals"),
        items: [
          {
            key: "goals_all",
            title: t("goals:goals"),
            description: t("goals:notification_settings_goals_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            anyPlanFeatures: [ApiFeatureType.goals],
          },
          {
            key: "goal_key_results_all",
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            title: t("goals:notification_settings_key_results_title"),
            description: t("goals:notification_settings_key_results_caption"),
            anyPlanFeatures: [ApiFeatureType.goals],
          },
        ],
      },
      {
        title: t("surveys:surveys"),
        items: [
          {
            key: "talent_surveys_all",
            title: t("surveys:surveys"),
            description: t("surveys:surveys_notification_description"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            isInApp: true,
            anyPlanFeatures: [ApiFeatureType.talent_surveys],
          },
        ],
      },
      {
        title: t("performance_review:performance_reviews"),
        items: [
          {
            key: "talent_performance_reviews_all",
            title: t("performance_review:performance_reviews"),
            description: t("performance_review:performance_review_notification_description"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            isInApp: true,
            anyPlanFeatures: [ApiFeatureType.talent_performance_reviews],
          },
        ],
      },
      {
        title: t("ats:recruit"),
        items: [
          {
            key: "mention",
            title: t("core:mentions"),
            description: t("ats:notification_settings_mentions_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            anyPlanFeatures: [ApiFeatureType.ats],
          },
          {
            key: "ats_comments_all",
            title: t("common:comments"),
            description: t("ats:notification_settings_comments_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            anyPlanFeatures: [ApiFeatureType.ats],
          },
          {
            key: "hiring_plan_all",
            title: t("hiring_plan:hiring_plan"),
            description: t("hiring_plan:notification_settings_hiring_plan_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            isInApp: true,
            anyOfViewPermissions: [ApiPermission.manage_hiring_plan],
            anyPlanFeatures: [ApiFeatureType.ats],
          },
          {
            key: "data_protection_response",
            title: t("data_protection:gdpr_consent"),
            description: t("data_protection:notification_settings_gdpr_consent_caption"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            anyPlanFeatures: [ApiFeatureType.ats_gdpr],
          },
          {
            key: "ats_feedbacks_all",
            title: t("scorecards:feedbacks"),
            description: t("scorecards:notification_settings_feedbacks"),
            availableOptions: [
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
              ApiNotificationChannelType.messenger,
            ],
            anyPlanFeatures: [ApiFeatureType.ats_scorecards],
          },
        ],
      },
    ];
  };

  const mapNotificationChannelsToItems = (
    channels: ApiNotificationChannelType[]
  ): NotificationChannelItem[] => {
    const allItems = NotificationChannelItems();
    return allItems.filter((item) => channels.includes(item.id as ApiNotificationChannelType));
  };

  const getNotificationsSettingVisibleToEmployee = (
    employee: ApiMeEmployee
  ): NotificationSettingSection[] => {
    const result: NotificationSettingSection[] = [];
    const settingSections = getNotificationsSettingSections();

    for (let sectionIndex = 0; sectionIndex < settingSections.length; sectionIndex++) {
      const showItems: NotificationSettingItem[] = [];

      for (let itemIndex = 0; itemIndex < settingSections[sectionIndex].items.length; itemIndex++) {
        const item = settingSections[sectionIndex].items[itemIndex];

        const anyFeaturesEnabled = item.anyPlanFeatures?.length
          ? item.anyPlanFeatures.some((feature) => employee.plan?.features[feature]?.enabled)
          : true;
        if (!anyFeaturesEnabled) continue;

        const anyOfViewPermissions = item.anyOfViewPermissions?.length
          ? item.anyOfViewPermissions.some(
              (field) =>
                employee.api_permissions[field] === PermissionType.view ||
                employee.api_permissions[field] === PermissionType.edit
            )
          : true;

        if (!anyOfViewPermissions) continue;

        showItems.push(item);
      }

      if (showItems.length > 0) {
        result.push({
          ...settingSections[sectionIndex],
          items: showItems,
        });
      }
    }

    return result;
  };

  return {
    getNotificationsSettingVisibleToEmployee,
    mapNotificationChannelsToItems,
  };
}
